import type {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { createContext, useCallback } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  FinanceResult,
  GetDashboardParams,
  GetDashboardResult,
  GetPayinsParams,
  GetPayinsResult,
  GetPayoutsParams,
  GetPayoutsResult,
  IReportingPhoenixApi,
  ReceiptStatusCount,
  ReceiptTypeCount,
  ResidentWithBalance,
} from './api';
import { ReportingPhoenixApi } from './api';
import { format } from 'date-fns';

const ApiContext = createContext<IReportingPhoenixApi | undefined>(undefined);
const {
  useApi: useReportingPhoenixApi,
  ApiProvider: ReportingPhoenixApiProvider,
} = makeApiProvider({
  name: 'ReportingPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new ReportingPhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useReportingPhoenixApi,
  ReportingPhoenixApiProvider,
  ApiContext as ReportingPhoenixApiContext,
  FinanceResult,
  GetPayoutsResult,
  GetPayoutsParams,
  GetPayinsResult,
  GetPayinsParams,
  GetDashboardResult,
  GetDashboardParams,
  ReceiptStatusCount,
  ReceiptTypeCount,
  ResidentWithBalance,
};

export const getFinanceKey = (nursingHomeId: string | undefined): QueryKey => [
  'GET_FINANCE',
  nursingHomeId,
];

export const useGetFinancePhoenix = ({
  nursingHomeId,
  options,
}: {
  nursingHomeId: string | undefined;
  options?: Omit<
    UseQueryOptions<FinanceResult, unknown>,
    'queryFn' | 'queryKey'
  >;
}): UseQueryResult<FinanceResult, unknown> => {
  return useApiQuery(
    useReportingPhoenixApi,
    getFinanceKey(nursingHomeId),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('Missing nursing home ID');
      }
      return api.getFinance({
        params: {
          nursing_home_id: nursingHomeId,
        },
      });
    },
    {
      ...options,
      enabled: nursingHomeId !== undefined,
    }
  );
};

export const useGetPayoutsPhoenix = (
  params: GetPayoutsParams,
  options?: Omit<
    UseQueryOptions<GetPayoutsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetPayoutsResult, unknown> => {
  const select = useCallback((data: GetPayoutsResult) => {
    return {
      ...data,
      data: data.data.map((payout) => ({
        ...payout,
        inserted_at: new Date(payout.inserted_at),
        transferred: payout.transferred
          ? new Date(payout.transferred)
          : undefined,
      })),
    };
  }, []);
  return useApiQuery(
    useReportingPhoenixApi,
    ['GET_PAYOUTS_PHOENIX', params.params],
    (api) => api.getPayouts(params),
    {
      ...(options ?? {}),
      select,
    }
  );
};

export const useGetPayinsPhoenix = (
  params: GetPayinsParams,
  options?: Omit<
    UseQueryOptions<GetPayinsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetPayinsResult, unknown> => {
  const select = useCallback((data: GetPayinsResult) => {
    return {
      ...data,
      data: data.data.map((payin) => ({
        ...payin,
        action_timestamp: new Date(payin.action_timestamp),
        updated_at: new Date(payin.updated_at),
        charge_date: new Date(payin.charge_date),
      })),
    };
  }, []);
  return useApiQuery(
    useReportingPhoenixApi,
    ['GET_PAYINS_PHOENIX', params.params],
    (api) => api.getPayins(params),
    {
      ...(options ?? {}),
      select,
    }
  );
};

export const getDashboardKey = (
  nursingHomeId: string | undefined,
  dateString: string
): QueryKey => ['GET_DASHBOARD', nursingHomeId, dateString];

export const useGetDashboardPhoenix = ({
  nursingHomeId,
  month,
  options,
}: {
  nursingHomeId: string | undefined;
  month?: Date;
  options?: Omit<
    UseQueryOptions<GetDashboardResult, unknown>,
    'queryFn' | 'queryKey'
  >;
}): UseQueryResult<GetDashboardResult, unknown> => {
  const select = useCallback((data: GetDashboardResult) => {
    const transformedData = {
      ...data,
      data: {
        ...data.data,
        negative_balance: {
          ...data.data.negative_balance,
          residents: data.data.negative_balance.residents.map((resident) => ({
            ...resident,
            due_date: new Date(resident.due_date),
          })),
        },
        balance_below_threshold: {
          ...data.data.balance_below_threshold,
          residents: data.data.balance_below_threshold.residents.map(
            (resident) => ({
              ...resident,
              due_date: new Date(resident.due_date),
            })
          ),
        },
        date_range: {
          ...data.data.date_range,
          from_date: new Date(data.data.date_range.from_date),
          to_date: new Date(data.data.date_range.to_date),
        },
      },
    };
    return transformedData;
  }, []);

  const dateString = month
    ? format(month, 'yyyy-MM')
    : format(new Date(), 'yyyy-MM');

  return useApiQuery(
    useReportingPhoenixApi,
    getDashboardKey(nursingHomeId, dateString),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('Missing nursing home ID');
      }
      return api.getDashboard({
        params: {
          nursing_home_id: nursingHomeId,
          month: dateString,
        },
      });
    },
    {
      ...(options ?? {}),
      enabled: nursingHomeId !== undefined,
      select,
    }
  );
};
